import { useEffect, useRef, useState } from 'react';
import { ChevronDown, ExternalLink, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useNavigateWithContentFlush } from '@/hooks';
import { toContentHref } from '@/utils/content-ref';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { KeepUnderProjectDialog } from './KeepUnderProjectDialog';

export interface KeptRef {
  contentId: number;
  code: string;
}

export interface KeepActionsProps {
  kept: KeptRef | null;
  onKeep: (anchorId?: number) => void;
  pending: boolean;
  error: unknown;
  label?: string;
  description: string;
}

type PressedAction = 'keep' | 'project' | null;

export function KeepActions({ kept, onKeep, pending, error, label = 'Keep', description }: KeepActionsProps) {
  const navigate = useNavigateWithContentFlush();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pressed, setPressed] = useState<PressedAction>(null);
  const sawPending = useRef(false);

  useEffect(() => {
    if (pending) {
      sawPending.current = true;
      return;
    }
    if (!sawPending.current) return;
    sawPending.current = false;
    if (pressed === 'project' && (error === null || error === undefined)) {
      setDialogOpen(false);
    }
    setPressed(null);
  }, [pending, error, pressed]);

  if (kept !== null) {
    return (
      <button
        type="button"
        onClick={() => navigate(toContentHref(kept.code))}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground"
      >
        <ExternalLink className="h-3 w-3 shrink-0" />
        In Vivport
      </button>
    );
  }

  return (
    <div className="min-w-0">
      <div className="inline-flex items-center">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="rounded-r-none"
          disabled={pending}
          onClick={() => {
            setPressed('keep');
            onKeep();
          }}
        >
          {pending ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : null}
          {label}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="-ml-px rounded-l-none px-2"
              disabled={pending}
              aria-label={`${label} options`}
            >
              <ChevronDown className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            <DropdownMenuItem onSelect={() => setDialogOpen(true)}>Keep under project…</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {error !== null && error !== undefined ? (
        <p className="mt-1 text-xs text-destructive">{getAxiosErrorDisplayMessage(error, 'Could not keep this')}</p>
      ) : null}
      <KeepUnderProjectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onConfirm={(anchorId) => {
          setPressed('project');
          onKeep(anchorId);
        }}
        pending={pending && pressed === 'project'}
        description={description}
      />
    </div>
  );
}
